
import { useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";

interface PricingTierConfig {
  context?: 'welcome' | 'settings';
  subscriptionStatus: string;
  isLoading: boolean;
  isLoadingStatus: boolean;
  handleUpgrade: () => Promise<void>;
  onFreeClick?: () => void;
  onChurchContact: () => Promise<void>;
}

export function usePricingTiers({
  context = 'settings',
  subscriptionStatus,
  isLoading,
  isLoadingStatus,
  handleUpgrade,
  onFreeClick,
  onChurchContact
}: PricingTierConfig) {
  const navigate = useNavigate();
  const session = useSession();

  const handleFreeClick = () => {
    window.scrollTo(0, 0);
    
    if (onFreeClick) {
      onFreeClick();
    } else {
      // For settings context, redirect to verse dashboard
      navigate('/');
    }
  };

  if (context === 'welcome') {
    return [
      {
        title: "Free",
        price: 0,
        features: [
          "Daily Bible Verses",
          "Basic Prayer Requests",
          "Community Access"
        ],
        buttonText: "Continue with Free",
        buttonVariant: "outline" as const,
        onButtonClick: handleFreeClick,
        className: "hover:border-grace-DEFAULT transition-colors duration-300"
      },
      {
        title: "Premium",
        price: 4.99,
        features: [
          "Everything in Free",
          "Daily devotionals",
          "Prayer requests",
          "Journal feature",
          "Scripture journeys",
          "NFC bracelet integration"
        ],
        buttonText: "Go Premium",
        isPopular: true,
        onButtonClick: handleUpgrade
      }
    ];
  }
  
  return [
    {
      title: "Free",
      price: 0,
      features: [
        "Daily Bible verse",
        "Basic verse sharing"
      ],
      buttonText: subscriptionStatus === 'free' ? "Current Plan" : "Basic Plan",
      buttonVariant: "outline" as const,
      onButtonClick: handleFreeClick,
      isCurrentPlan: subscriptionStatus === 'free',
      isLoading: isLoadingStatus
    },
    {
      title: "Premium",
      price: 4.99,
      features: [
        "All Free features",
        "Daily devotionals",
        "Bible study plans",
        "Prayer requests",
        "Journal feature",
        "Community access"
      ],
      buttonText: subscriptionStatus === 'premium' ? "Current Plan" : "Upgrade Now",
      isPopular: true,
      isLoading: isLoading || isLoadingStatus,
      onButtonClick: subscriptionStatus === 'premium' ? undefined : handleUpgrade,
      buttonDisabled: subscriptionStatus === 'premium' || isLoading || isLoadingStatus,
      isCurrentPlan: subscriptionStatus === 'premium'
    },
    {
      title: "Church",
      price: 49.99,
      features: [
        "All Premium features",
        "Analytics dashboard",
        "Custom branding",
        "Bulk NFC management",
        "Weekly sermon plans based on congregation interests",
        "Insights from prayer requests and journals",
        "Priority support"
      ],
      buttonText: "Contact Sales",
      buttonVariant: "outline" as const,
      isLoading: isLoadingStatus,
      onButtonClick: onChurchContact
    }
  ];
}
